<template>
  <div class="c-task-dialog">
    <v-dialog
      v-model="showDialog"
      scrollable
      max-width="95vw"
    >
      <v-card class="card-overflow">
        <v-card-title>
          {{ taskData.Name ? 'Edit' : 'Create' }} {{ taskData.Is_Folder ? 'folder': 'task' }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="closeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-form v-model="form">
            <v-container>
              <v-row>
                <!-- Left side -->
                <v-col cols="12" md="6">
                  <v-row>
                    <!-- Task name -->
                    <v-col cols="12">
                      <v-text-field
                        :label="taskData.Is_Folder ? 'Folder name': 'Task name'"
                        v-model="taskData.Name"
                        counter="100"
                        hide-details="auto"
                        :rules="[rules.maxLength(100)]"
                        :disabled="taskData.Jira_Task_Key"
                      ></v-text-field>
                    </v-col>
                    <!-- /Task name -->
                    <!-- Start date -->
                    <v-col cols="6">
                      <DatePicker
                        v-model="taskData.Start_Date"
                        :disabled="taskData.Jira_Task_Key"
                      />
                    </v-col>
                    <!-- /Start date -->
                    <!-- Estimate -->
                    <v-col cols="6" v-if="!taskData.Is_Folder">
                      <v-text-field
                        label="Estimate"
                        type="Number"
                        v-model="taskData.Estimate"
                      ></v-text-field>
                    </v-col>
                    <!-- /Estimate -->
                    <!-- Employee list -->
                    <v-col cols="12" md="6">
                      <v-select
                        v-if="!createFromFolder"
                        v-model="taskData.Assigned_To"
                        :items="employeeList"
                        label="Assigned to"
                        item-text="full_name"
                        item-value="employee_id"
                        ref="employeeSelect"
                        :disabled="taskData.Jira_Task_Key"
                      >
                        <template v-slot:prepend-item>
                          <div class="px-4">
                            <v-text-field
                              label="Search employee"
                              v-model="searchEmployee"
                            ></v-text-field>
                          </div>
                          <v-divider></v-divider>
                          <v-list-item
                            dense
                            link
                            @click="setSelection(null)"
                          >
                            <v-list-item-content>
                              <v-list-item-title>
                                --------
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item
                            dense
                            link
                            v-for="user in usersOnProject"
                            :key="user.employee_id"
                            @click="setSelection(user.employee_id)"
                          >
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ user.full_name }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider class="mt-2"></v-divider>
                        </template>
                      </v-select>
                    </v-col>
                    <!-- /Employee list -->
                    <!-- Parent tasks list -->
                    <v-col cols="12" md="6">
                      <v-select
                        v-if="!createFromFolder"
                        v-model="taskData.Parent_Task_ID"
                        :items="parentTasks"
                        :disabled="taskData.Jira_Task_Key"
                        label="Child for"
                        item-text="Name"
                        item-value="Task_ID"
                        @change="changeParent"
                      >
                      </v-select>
                    </v-col>
                    <!-- /Parent tasks list -->
                    <!-- Access to create tasks -->
                    <v-col cols="12" md="6" v-if="taskData.Task_ID && taskData.Is_Folder">
                      <v-select
                        v-model="accessToCreateTasks"
                        :items="employeeList"
                        multiple
                        label="Access To Create Tasks"
                        item-text="full_name"
                        item-value="employee_id"
                        clearable
                        @change="searchEmployee = null"
                      >
                        <template v-slot:prepend-item>
                          <div class="px-4">
                            <v-text-field
                              label="Search employee"
                              v-model="searchEmployee"
                            ></v-text-field>
                          </div>
                          <v-divider class="mt-2"></v-divider>
                        </template>
                      </v-select>
                    </v-col>
                    <!-- /Access to create tasks -->
                  </v-row>
                </v-col>
                <!-- /Left side -->
                <!-- Right side -->
                <v-col cols="12" md="6">
                  <v-row>
                    <!-- Status -->
                    <v-col cols="6" md="9">
                      <v-select
                        v-model="taskData.Status"
                        :items="taskStatuses"
                        label="Status"
                        :disabled="taskData.Jira_Task_Key"
                      >
                      </v-select>
                    </v-col>
                    <!-- /Status -->
                    <!-- Show in report -->
                    <v-col cols="6" md="3">
                      <v-switch
                        v-model="taskData.Show_IN_Report"
                        label="Show in report"
                        color="primary"
                        hide-details
                        :disabled="taskData.Jira_Task_Key"
                      ></v-switch>
                    </v-col>
                    <!-- /Show in report -->
                    <!-- Rate -->
                    <v-col cols="12" md="9" v-if="!taskData.Is_Folder">
                      <v-text-field
                        label="Rate"
                        type="Number"
                        :min="1"
                        v-model="taskData.Rate"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <!-- /Rate -->
                    <!-- Description -->
                    <v-col cols="12" :class="taskData.Is_Folder ? 'mt-folder' : 'mt-task'">
                      <div class="description-container">
                        <DescriptionWithSpellCheck
                          :key="'textarea-' + showDialog"
                          label="Description"
                          v-model="taskData.Description"
                        />
                        <BadWords
                          v-if="!taskData.Is_Folder"
                          :description="taskData.Description"
                          :hours="taskData.Estimate"
                          :bad-words="badWords"
                        />
                      </div>
                    </v-col>
                    <!-- /Description -->
                  </v-row>
                </v-col>
                <!-- /Right side -->
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            small
            :disabled="!form"
            @click.prevent="saveTask"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    <ConfirmOrDeniedDialog
      v-model="showDeniedDialog"
      v-if="showDeniedDialog"
      :mode="confirmOrDeniedDialogModes.denied"
      :title="deniedTitle"
      :message="deniedMessage"
    />
    </v-dialog>
  </div>
</template>

<script>
import { sharedReportingMixin } from '@/mixins/sharedReportingMixin'
import { getApiErrorMessage } from '@/utils/apiError'

export default {
  name: 'TaskDialog',
  mixins: [sharedReportingMixin],
  components: {
    DescriptionWithSpellCheck: () => import('@/components/reporting/DescriptionWithSpellCheck.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue'),
    BadWords: () => import('@/components/global/BadWords.vue')
  },
  props: {
    createFromFolder: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      form: false,
      searchEmployee: '',
      deniedTitle: '',
      deniedMessage: '',
      showDeniedDialog: false,
      taskStatuses: ['Completed', 'Development', 'Hold', 'Open', 'QA'],
      parentTaskChanged: false,
      primaryTask: null,
      newParent: null,
      accessToCreateTasks: [],
      badWords: [],
      rules: {
        maxLength: len => v => (v || '').length <= len
      }
    }
  },
  async mounted () {
    this.primaryTask = JSON.parse(JSON.stringify(this.taskData))
    await this.getBadWords()
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    usersOnProject () {
      const usersOnProject = this.$store.getters.getUsersOnProject
      if (this.searchEmployee) {
        const reg = new RegExp(this.searchEmployee.toLowerCase(), 'gi')
        return this.$store.getters.getEmployee.filter(item => {
          reg.lastIndex = 0
          return usersOnProject.indexOf(item.employee_id) > -1 && reg.test(item.full_name.toLowerCase())
        })
      } else {
        return this.$store.getters.getEmployee.filter(item => usersOnProject.indexOf(item.employee_id) > -1)
      }
    },
    parentTasks () {
      const tasks = this.$store.getters.getParentTasksInProject
      return tasks
    },
    employeeList () {
      if (this.searchEmployee) {
        const reg = new RegExp(this.searchEmployee.toLowerCase(), 'gi')
        return this.$store.getters.getEmployee.filter(item => {
          reg.lastIndex = 0
          return reg.test(item.full_name.toLowerCase())
        })
      } else return this.$store.getters.getEmployee
    },
    showDialog: {
      get () {
        if (this.$store.getters.showTaskDialog && this.taskData.Task_ID && this.taskData.Is_Folder) this.getAccessToCreateTasks()
        return this.$store.getters.showTaskDialog
      },
      set (val) {
        this.accessToCreateTasks = []
        this.$store.dispatch('showTaskDialog', { show: val })
      }
    },
    taskData: {
      get () {
        return this.$store.getters.getCurrentTaskData
      },
      set (newVal) {
        this.$store.dispatch('setCurrentTaskData', { taskData: newVal })
      }
    }
  },
  methods: {
    changeParent (e) {
      this.parentTaskChanged = true
      this.newParent = this.parentTasks.find(el => el.Task_ID === e)
    },
    getAccessToCreateTasks () {
      this.axios.get(`/api/tasks/access-to-create-tasks/${this.taskData.Project_ID}/${this.taskData.Task_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.accessToCreateTasks = []
        for (let i = 0; i < response.data.length; i++) {
          this.accessToCreateTasks.push(response.data[i].Employee_ID)
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    postAccessToCreateTasks () {
      this.axios.post('/api/tasks/access-to-create-tasks', {
        Task_ID: this.taskData.Task_ID,
        Project_ID: this.taskData.Project_ID,
        Access_To_Create_Tasks: this.accessToCreateTasks
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('updateTasks', { update: true })
        this.$store.dispatch('setSuccess', { successMsg: 'Task successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    setSelection (userId) {
      this.$nextTick(() => {
        this.taskData.Assigned_To = userId
        this.searchEmployee = ''
        this.$refs.employeeSelect.blur()
      })
    },
    closeDialog () {
      this.$store.dispatch('showTaskDialog', { show: false })
    },
    saveTask () {
      if (this.taskData.Task_ID) this.updateTask(this.$store.getters.getCurrentTaskData)
      else this.createNewTaskTask(this.$store.getters.getCurrentTaskData)
    },
    normalizeNumericFields (task) {
      // deep clone to avoid mutating original reactive task
      const payload = JSON.parse(JSON.stringify(task))

      const numericFields = ['Rate', 'Estimate', 'Reported']
      numericFields.forEach(f => {
        if (Object.hasOwn(payload, f)) {
          // treat empty string/whitespace/"null"/undefined as null
          if (payload[f] === '' || payload[f] === null || payload[f] === undefined) {
            payload[f] = null
          } else {
            // try to convert to number; if not a valid number -> null
            const n = Number(payload[f])
            payload[f] = Number.isFinite(n) ? n : null
          }
        }
      })

      // boolean/string normalization
      if (Object.hasOwn(payload, 'Show_IN_Report')) {
        payload.Show_IN_Report = !!payload.Show_IN_Report
      }

      return payload
    },
    updateTask (data) {
      if (this.parentTaskChanged && this.newParent.Status && this.primaryTask.Status !== this.newParent.Status) {
        this.deniedTitle = 'Moving Task'
        this.deniedMessage = 'Tasks or folder status is not the same as the destination folder'
        this.showDeniedDialog = true
        return
      }

      let query = `/api/tasks/${this.taskData.Task_ID}`
      if (data.Is_Folder && data.Status !== this.primaryTask.Status) {
        query += `/${true}`
      }
      const payload = this.normalizeNumericFields(data)
      this.axios.put(query, payload, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.closeDialog()
        this.postAccessToCreateTasks()
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    createNewTaskTask (data) {
      this.axios.post('/api/tasks', data, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.closeDialog()
        this.$store.dispatch('setSuccess', { successMsg: 'Task successfully created' })
        this.$store.dispatch('updateStateOfOpenedFolders', {
          taskId: data.Parent_Task_ID,
          isOpen: true
        })
        this.$store.dispatch('updateTasks', { update: true })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>

<style lang="scss">
.v-menu__content {
  background-color: #fff;
}
.description-container {
  position: relative;
  .bad-words-indicator {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
  }
}
.mt-folder {
  margin-top: 48px;
}
.mt-task {
  margin-top: 70px;
}
.card-overflow {
  overflow-x: hidden !important;
}
</style>
